import { useMemo, useState, type KeyboardEvent } from 'react';
import { Field, Input, IconButton } from '@bb/ui';
import type { CanvasValidationIssue } from '../../../api/client.ts';
import { AudiencePathsPanel, type AudiencePathsValue } from '../AudiencePathsPanel.tsx';
import type { Step } from './commands.ts';
import { ActionPathsPanel } from './steps/ActionPathsPanel.tsx';
import { contextVariablesBefore } from './steps/contextConfig.ts';
import { ContextStepPanel, type EarlierVariableView } from './steps/ContextStepPanel.tsx';
import { DecisionSplitPanel } from './steps/DecisionSplitPanel.tsx';
import { DelayStepPanel } from './steps/DelayStepPanel.tsx';
import { MessageStepPanel } from './steps/MessageStepPanel.tsx';
import { UserUpdatePanel } from './steps/UserUpdatePanel.tsx';

export interface StepInspectorProps {
  environmentId: string;
  step: Step;
  /** Every step of the draft being edited (the Context panel lists what earlier steps set). */
  steps: Readonly<Record<string, Step>>;
  typeLabel: string;
  /** The type has a runtime handler; otherwise it can be designed but not sent yet. */
  runtime: boolean;
  /** Problems the draft validation found in this step. */
  issues: CanvasValidationIssue[];
  /** One-line summary from the draft validation. */
  summary: string | undefined;
  readOnly: boolean;
  /** Why it is read-only (no `canvas.edit`, archived). */
  readOnlyReason?: string | undefined;
  /** A paths step of the running version: the order and the set of its groups cannot change. */
  locked: boolean;
  /** The step is in the running version (a card's creation mode is fixed). */
  launched: boolean;
  /** The employee may ask the server for previews that need `canvas.edit` (Delay wake-up, Context). */
  canPreview: boolean;
  onConfigChange: (config: unknown) => void;
  onRename: (name: string) => void;
  onClose: () => void;
}

/** Shown by the User Update panel itself, beside the actions (BB-120). */
const REENTRY_WARNING = 'user_update_reenters';

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** The stored Audience Paths config as the panel edits it (the defaults of `AudiencePathsConfig`). */
const audiencePathsValue = (config: unknown): AudiencePathsValue => {
  const c = isObject(config) ? config : {};
  return { groups: Array.isArray(c.groups) ? (c.groups as AudiencePathsValue['groups']) : [], everyoneElse: isObject(c.everyoneElse) ? (c.everyoneElse as AudiencePathsValue['everyoneElse']) : {} };
};

/** The step name, committed on Enter or when the field is left (Escape puts it back). */
function StepName({ name, disabled, onRename }: { name: string; disabled: boolean; onRename: (name: string) => void }) {
  const [text, setText] = useState(name);
  const [synced, setSynced] = useState(name);
  if (name !== synced) {
    setSynced(name);
    setText(name);
  }
  const commit = () => (text.trim() ? onRename(text) : setText(name));
  return (
    <Field label="Step name">
      <Input
        value={text}
        maxLength={120}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setText(name);
        }}
      />
    </Field>
  );
}

/** What earlier Context steps set on the way to a step, with the names of those steps (BB-119). */
function useEarlierContext(steps: Readonly<Record<string, Step>>, stepId: string, enabled: boolean): EarlierVariableView[] {
  return useMemo(() => (enabled ? contextVariablesBefore(steps, stepId).map((v) => ({ name: v.name, types: v.types, from: v.stepIds.map((id) => steps[id]?.name ?? id) })) : []), [steps, stepId, enabled]);
}

/**
 * Settings of the one selected step (BB-115…BB-120, BB-125): its name, what the draft validation says about it and the
 * panel of its type. Every edit goes to the draft through the editor (undo, unsaved changes). `nokey` keeps React
 * Flow's Delete shortcut away from the fields. Render it keyed by the step id: the local state of the panels (push mode,
 * units, test user, preview examples) belongs to one step.
 */
export function StepInspector({ environmentId, step, steps, typeLabel, runtime, issues, summary, readOnly, readOnlyReason, locked, launched, canPreview, onConfigChange, onRename, onClose }: StepInspectorProps) {
  const earlierContext = useEarlierContext(steps, step.id, step.type === 'context');
  const listed = step.type === 'user_update' ? issues.filter((i) => i.code !== REENTRY_WARNING) : issues;
  const panel = (() => {
    switch (step.type) {
      case 'message':
        return <MessageStepPanel environmentId={environmentId} value={step.config} readOnly={readOnly} {...(readOnlyReason ? { readOnlyNotice: readOnlyReason } : {})} launched={launched} onChange={onConfigChange} />;
      case 'delay':
        return <DelayStepPanel environmentId={environmentId} value={step.config} readOnly={readOnly} canPreview={canPreview} onChange={onConfigChange} />;
      case 'action_paths':
        return <ActionPathsPanel environmentId={environmentId} value={step.config} readOnly={readOnly} locked={locked} onChange={onConfigChange} />;
      case 'audience_paths':
        return <AudiencePathsPanel environmentId={environmentId} value={audiencePathsValue(step.config)} readOnly={readOnly} locked={locked} onChange={onConfigChange} />;
      case 'context':
        return <ContextStepPanel environmentId={environmentId} value={step.config} readOnly={readOnly} earlier={earlierContext} canPreview={canPreview} onChange={onConfigChange} />;
      case 'decision_split':
        return <DecisionSplitPanel environmentId={environmentId} value={step.config} readOnly={readOnly} onChange={onConfigChange} />;
      case 'user_update':
        return (
          <UserUpdatePanel
            environmentId={environmentId}
            value={step.config}
            readOnly={readOnly}
            serverFields={issues.flatMap((i) => (i.code === 'invalid_step_config' ? (i.fields ?? []) : []))}
            reentryWarnings={issues.filter((i) => i.code === REENTRY_WARNING).map((i) => i.message)}
            onChange={onConfigChange}
          />
        );
      default:
        return <p className="hint">{typeLabel} settings are edited once their ticket brings the step; what is stored is kept.</p>;
    }
  })();
  return (
    <aside className="canvas-inspector nokey" aria-label={`Step settings: ${step.name}`}>
      <header className="canvas-inspector__head">
        <span className="canvas-inspector__type">{typeLabel}</span>
        <IconButton label="Close step settings" variant="ghost" size="sm" icon={<span aria-hidden="true">×</span>} onClick={onClose} />
      </header>
      <StepName name={step.name} disabled={readOnly} onRename={onRename} />
      {summary && <p className="canvas-inspector__summary">{summary}</p>}
      {!runtime && <p className="notice notice--warning">{typeLabel} steps can be designed; sending them comes with their ticket.</p>}
      {readOnly && readOnlyReason && <p className="hint">{readOnlyReason}</p>}
      {listed.length > 0 && (
        <ul aria-label="Problems of this step" className="canvas-inspector__issues">
          {listed.map((issue, i) => (
            <li key={`${issue.code}-${i}`} className={issue.severity === 'error' ? 'field-error' : 'hint'}>
              {issue.message}
            </li>
          ))}
        </ul>
      )}
      {panel}
    </aside>
  );
}
